import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, X } from 'lucide-react';
import type { ResourceMeta } from '../../types/resource';
import type { SubmissionRow, UpdateSubmissionBody } from '../../types/submission';
import { useSubmission } from '../../hooks/useSubmission';
import { useUpdateSubmission } from '../../hooks/useUpdateSubmission';
import { coerceFieldValueForWire } from '../../utils/fieldValue';
import { extractEditableFields } from './extractEditableFields';
import { FieldEditor } from './FieldEditor';

interface ResourceEditDialogProps {
  meta: ResourceMeta;
  resource: string;
  /**
   * Row that opened the dialog. Provides `id` (required) and
   * whatever additional fields the caller happened to have on hand
   * — the dialog re-fetches the full row before rendering the form,
   * so summary-mode / stale-grid callers work without special-casing.
   */
  row: SubmissionRow;
  onClose: () => void;
  /** Called after a successful save so the parent can refetch the list. */
  onSaved: () => void;
}

/**
 * Edit dialog wrapper. Always fetches a fresh copy of the row before
 * rendering the form so:
 *
 *   - Rows opened from Card view (`?summary=true`) — which only ship
 *     the summary-view keys — get their full editable field set from
 *     `GET /submissions/:id`.
 *   - Rows opened from a grid that the reviewer has been staring at
 *     for a while get the latest `updated_at` for optimistic locking.
 *   - Deleted / moved rows surface a friendly not-found banner
 *     instead of a form-load crash.
 *
 * The inner `ResourceEditForm` only mounts once we have the fresh
 * row, so its `useState` initialisers see the correct seed values.
 */
export function ResourceEditDialog({
  meta,
  resource,
  row: initialRow,
  onClose,
  onSaved,
}: ResourceEditDialogProps) {
  const { row: freshRow, loading, error, refetch } = useSubmission(resource, initialRow.id);

  // Explicit tri-state routing so the dialog never hangs:
  //   - fetch in flight and nothing to show yet → spinner
  //   - fetch failed → error banner with a retry
  //   - fetch resolved with no row → not-found banner (row was
  //     deleted / moved / server sent an unexpected shape)
  //   - fetch resolved with a row → render the form
  if (loading && !freshRow) {
    return (
      <DialogShell resource={resource} rowId={initialRow.id} meta={meta} onClose={onClose}>
        <div className="flex items-center justify-center gap-2 px-6 py-16 text-sm text-rgf-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading submission…
        </div>
      </DialogShell>
    );
  }

  if (error) {
    return (
      <DialogShell resource={resource} rowId={initialRow.id} meta={meta} onClose={onClose}>
        <div className="mx-6 mt-6 mb-8 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm">
          <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600" />
          <div className="flex-1">
            <p className="font-medium text-red-900">Couldn't load this submission</p>
            <p className="mt-1 text-red-700">{error.message}</p>
            <button
              type="button"
              onClick={refetch}
              className="mt-3 inline-flex items-center rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
            >
              Try again
            </button>
          </div>
        </div>
      </DialogShell>
    );
  }

  if (!freshRow) {
    return (
      <DialogShell resource={resource} rowId={initialRow.id} meta={meta} onClose={onClose}>
        <div className="mx-6 mt-6 mb-8 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
          <AlertCircle className="h-5 w-5 flex-shrink-0 text-amber-700" />
          <div className="flex-1">
            <p className="font-medium text-amber-900">Submission not available</p>
            <p className="mt-1 text-amber-800">
              The server didn't return a record for this id. It may have been deleted, or
              the response envelope isn't one we recognise yet.
            </p>
            <button
              type="button"
              onClick={refetch}
              className="mt-3 inline-flex items-center rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-50"
            >
              Try again
            </button>
          </div>
        </div>
      </DialogShell>
    );
  }

  return (
    <ResourceEditForm
      meta={meta}
      resource={resource}
      row={freshRow}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}

interface DialogShellProps {
  meta: ResourceMeta;
  resource: string;
  rowId: number | string;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Shared modal chrome (backdrop + header + close). Reused by the
 * loading, error, and form states so those transitions don't cause
 * the dialog to jump size or reposition.
 */
function DialogShell({ meta, resource, rowId, onClose, children }: DialogShellProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-6">
      <div className="mt-8 w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-rgf-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-rgf-gray-900">Edit submission</h2>
            <p className="mt-0.5 text-xs text-rgf-gray-500">
              {meta.display_title || resource} · id {String(rowId)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-rgf-gray-500 hover:bg-rgf-gray-100 hover:text-rgf-gray-900"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

interface ResourceEditFormProps {
  meta: ResourceMeta;
  resource: string;
  row: SubmissionRow;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * The actual edit form. Only mounted after the parent has a fresh
 * row, so state initialisers see the right values.
 *
 * Optimistic locking:
 *   - Snapshots `row.updated_at` at open time.
 *   - Sends it back as `previous_updated_at` on PATCH.
 *   - On STALE_RECORD (409), surfaces a "conflict" banner asking the
 *     reviewer to reload and re-apply changes.
 *
 * `change_reason` is required by the server on every PATCH — we
 * enforce that client-side too with an inline error so the user
 * isn't hit with a 422 after typing out their edits.
 */
function ResourceEditForm({
  meta,
  resource,
  row,
  onClose,
  onSaved,
}: ResourceEditFormProps) {
  const editableFields = useMemo(() => extractEditableFields(meta), [meta]);
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    editableFields.reduce<Record<string, unknown>>((acc, f) => {
      acc[f.title] = row[f.title];
      return acc;
    }, {}),
  );
  const [changeReason, setChangeReason] = useState('');
  const [previousUpdatedAt] = useState<string | null>(row.updated_at ?? null);
  const [staleConflict, setStaleConflict] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const { update, saving, error } = useUpdateSubmission(resource, row.id);

  useEffect(() => {
    // Reset stale flag whenever the caller swaps the row underneath us.
    setStaleConflict(false);
  }, [row.id]);

  const setValue = useCallback((title: string, next: unknown) => {
    setValues((prev) => ({ ...prev, [title]: next }));
  }, []);

  const changedFields = useMemo(() => {
    const changed: Record<string, unknown> = {};
    for (const field of editableFields) {
      const current = values[field.title];
      const original = row[field.title];
      if (!isSameValue(current, original)) {
        changed[field.title] = coerceFieldValueForWire(field, current);
      }
    }
    return changed;
  }, [editableFields, values, row]);

  // Enable Save as soon as there's a real change to send. Missing
  // `change_reason` is caught on submit with an inline error so the
  // reviewer sees exactly *why* the click didn't go through — a
  // disabled button with no explanation was the reported UX bug.
  const canSubmit = Object.keys(changedFields).length > 0 && !saving;

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setAttemptedSubmit(true);
      if (changeReason.trim().length === 0) return;
      if (Object.keys(changedFields).length === 0) return;

      const body: UpdateSubmissionBody = {
        change_reason: changeReason.trim(),
        previous_updated_at: previousUpdatedAt,
        fields: changedFields,
      };
      try {
        await update(body);
        onSaved();
        onClose();
      } catch (err) {
        // useUpdateSubmission already normalised the error into `error`.
        // Detect the stale-record case here so we can render the banner.
        const status = (err as { response?: { status?: number } })?.response?.status;
        const code = ((err as { response?: { data?: { code?: string } } })?.response?.data?.code || '').toString().toUpperCase();
        if (status === 409 || code === 'STALE_RECORD') setStaleConflict(true);
      }
    },
    [changeReason, changedFields, previousUpdatedAt, update, onSaved, onClose],
  );

  const changeReasonInvalid = attemptedSubmit && changeReason.trim().length === 0;

  return (
    <DialogShell meta={meta} resource={resource} rowId={row.id} onClose={onClose}>
        {staleConflict && (
          <div className="mx-6 mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0 text-amber-700" />
            <div className="text-amber-900">
              This record was updated by someone else while you were editing.
              Close this dialog and reopen it from the grid to pick up the latest
              values, then re-apply your changes.
            </div>
          </div>
        )}

        {error && !staleConflict && (
          <div className="mx-6 mt-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-700" />
            <div className="text-red-900">{error.message}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {editableFields.map((field) => (
              <FieldEditor
                key={field.title}
                field={field}
                value={values[field.title]}
                onChange={(next) => setValue(field.title, next)}
                disabled={saving}
                error={error?.fieldErrors?.[field.title]}
              />
            ))}
          </div>

          <div>
            <label className="block text-xs font-medium text-rgf-gray-700">
              Change reason <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="Why are you making this change?"
              aria-invalid={changeReasonInvalid || undefined}
              className={
                'mt-1.5 w-full rounded-lg border px-3 py-2 text-sm text-rgf-gray-900 focus:outline-none focus:ring-1 ' +
                (changeReasonInvalid
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
                  : 'border-rgf-gray-300 focus:border-rgf-primary-500 focus:ring-rgf-primary-500')
              }
              disabled={saving}
            />
            {changeReasonInvalid && (
              <p className="mt-1 text-xs text-red-600">A change reason is required before saving.</p>
            )}
          </div>

          <footer className="flex items-center justify-between gap-2 pt-2">
            <div className="text-xs text-rgf-gray-500">
              {Object.keys(changedFields).length === 0
                ? 'No changes yet.'
                : `${Object.keys(changedFields).length} field${Object.keys(changedFields).length === 1 ? '' : 's'} will be updated.`}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-rgf-gray-200 px-4 py-2 text-sm font-medium text-rgf-gray-700 hover:border-rgf-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="rounded-lg bg-rgf-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-rgf-primary-700 disabled:cursor-not-allowed disabled:bg-rgf-gray-300"
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </footer>
        </form>
    </DialogShell>
  );
}


function isSameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!isSameValue(a[i], b[i])) return false;
    return true;
  }
  if (typeof a === 'object' && typeof b === 'object') {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  // Loose equality for cases like `"5"` vs `5` from a number editor.
  return String(a) === String(b);
}
